import { useColorModeValue, Box, Text, Flex, HStack } from "@chakra-ui/react";
import {
  ResponsiveContainer,
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ReferenceArea,
  type TooltipProps,
} from "recharts";
import { useState, useEffect } from "react";

const CustomTooltip = ({
  active,
  payload,
  label,
}: TooltipProps<number, string>) => {
  const bgColor = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.600");
  const textColor = useColorModeValue("gray.800", "white");

  if (active && payload && payload.length) {
    const formattedDate = formatDateDisplay(label);

    return (
      <Box
        bg={bgColor}
        p={3}
        borderRadius="md"
        boxShadow="sm"
        border="1px solid"
        borderColor={borderColor}
        maxW="250px"
      >
        <Text fontWeight="bold" mb={2} fontSize="sm">
          {formattedDate}
        </Text>
        <Flex direction="column" gap={1}>
          {payload.map((entry: any, index: any) => (
            <Flex key={`tooltip-${index}`} align="center">
              <Box
                w="10px"
                h="10px"
                borderRadius={entry.dataKey === "consultas" ? "2px" : "full"}
                bg={entry.color}
                mr={2}
              />
              <Text color={textColor} fontSize="sm">
                {entry.name}: {entry.value.toLocaleString()}
              </Text>
            </Flex>
          ))}
        </Flex>
      </Box>
    );
  }
  return null;
};

const formatDateDisplay = (dateStr: string) => {
  if (!dateStr) return "";

  const parts = dateStr.split("-");
  if (parts.length === 3) {
    const [year, month, day] = parts;
    return `${day}/${month}/${year}`;
  } else if (parts.length === 2) {
    const [year, month] = parts;
    return `${month}/${year}`;
  } else if (parts.length === 4) {
    const [year, month, week] = parts;
    return `Semana ${week}, ${month}/${year}`;
  }
  return dateStr;
};

const formatXAxis = (tickItem: string) => {
  if (!tickItem) return "";

  const parts = tickItem.split("-");
  if (parts.length === 3) {
    const [month, day] = parts;
    return `${day}/${month}`;
  } else if (parts.length === 2) {
    const [year, month] = parts;
    return `${month}/${year}`;
  } else if (parts.length === 4) {
    return `S${parts[3]}`;
  }
  return tickItem;
};

interface ChartData {
  name: string;
  consultas: number;
  propostasPagas: number;
  [key: string]: any;
}

interface CustomChartComponentProps {
  data: ChartData[];
  startDate: string;
  endDate: string;
  onRangeSelect?: (start: string, end: string) => void;
  dataGrouping: string;
  onGroupingChange?: (grouping: string) => void;
}

const CustomChartComponent = ({
  data,
  startDate,
  endDate,
  onRangeSelect,
  dataGrouping = "daily",
}: CustomChartComponentProps) => {
  const barColor = "#008000";
  const lineColor = "#2B7BE8";
  const leftAxisColor = "#008000";
  const rightAxisColor = "#2B7BE8";

  const gridColor = useColorModeValue(
    "rgba(0, 0, 0, 0.1)",
    "rgba(255, 255, 255, 0.1)"
  );
  const axisLabelColor = useColorModeValue("gray.700", "gray.300");
  const brushColor = useColorModeValue(
    "rgba(0, 128, 0, 0.1)",
    "rgba(37, 160, 37, 0.1)"
  );
  const cardBg = useColorModeValue("white", "gray.800");
  const chartBg = useColorModeValue("#f8fbff", "gray.900");
  const borderColor = useColorModeValue("gray.200", "gray.700");

  const [refAreaLeft, setRefAreaLeft] = useState("");
  const [refAreaRight, setRefAreaRight] = useState("");

  const [maxConsultas, setMaxConsultas] = useState(0);
  const [maxPropostas, setMaxPropostas] = useState(0);

  const legendBgColor = useColorModeValue("gray.50", "gray.700");
  const legendBorderColor = useColorModeValue("gray.100", "gray.600");
  const maxPropostasBgColor = useColorModeValue("blue.50", "blue.900");
  const maxConsultasBgColor = useColorModeValue("green.50", "green.900");

  useEffect(() => {
    if (data && data.length > 0) {
      const maxC = Math.max(...data.map((item) => item.consultas));
      const maxP = Math.max(...data.map((item) => item.propostasPagas));
      setMaxConsultas(maxC);
      setMaxPropostas(maxP);
    }
  }, [data]);

  const calculateTickInterval = () => {
    if (data.length <= 7) return 0;
    if (data.length <= 15) return 1;
    return Math.floor(data.length / 10);
  };

  const handleMouseDown = (e: any) => {
    if (e && e.activeLabel) {
      setRefAreaLeft(e.activeLabel);
    }
  };

  const handleMouseMove = (e: any) => {
    if (refAreaLeft && e && e.activeLabel) {
      setRefAreaRight(e.activeLabel);
    }
  };

  const handleMouseUp = () => {
    if (refAreaLeft && refAreaRight) {
      let left = refAreaLeft;
      let right = refAreaRight;

      if (left > right) {
        [left, right] = [right, left];
      }

      const leftIndex = data.findIndex((item) => item.name === left);
      const rightIndex = data.findIndex((item) => item.name === right);

      if (leftIndex !== -1 && rightIndex !== -1 && onRangeSelect) {
        onRangeSelect(left, right);
      }
    }

    setRefAreaLeft("");
    setRefAreaRight("");
  };

  const getChartTitle = () => {
    if (!startDate || !endDate) return "Visão Geral";

    const start = new Date(startDate);
    const end = new Date(endDate);
    const diffDays = Math.ceil(
      (end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)
    );

    if (diffDays <= 45) {
      return "Visão Diária";
    } else if (diffDays <= 180) {
      return "Visão Semanal";
    } else {
      return "Visão Mensal";
    }
  };

  const renderLegend = (props: any) => {
    const { payload } = props;

    return (
      <Flex justify="center" align="center" gap={6} mt={2}>
        {payload.map((entry: any, index: any) => {
          const isBar = entry.dataKey === "consultas";
          return (
            <Flex
              key={`legend-${index}`}
              align="center"
              bg={legendBgColor}
              px={3}
              py={1}
              borderRadius="md"
              border="1px solid"
              borderColor={legendBorderColor}
            >
              <Box
                w="12px"
                h="12px"
                borderRadius={isBar ? "2px" : "full"}
                bg={entry.color}
                mr={2}
                border={isBar ? "none" : `1px solid ${entry.color}`}
              />
              <Text fontSize="sm" fontWeight="medium" color={entry.color}>
                {entry.value}
              </Text>
            </Flex>
          );
        })}
      </Flex>
    );
  };

  const formatRightAxisTick = (value: number) => {
    if (value >= 1000) {
      return `${(value / 1000).toFixed(0)}K`;
    }
    return value.toLocaleString();
  };

  const formatLeftAxisTick = (value: number) => {
    if (value >= 1000) {
      return `${(value / 1000).toFixed(0)}K`;
    }
    return value.toLocaleString();
  };

  const calculateRightYAxisDomain = () => {
    if (maxPropostas === 0) return [0, 10];
    const maxValue = Math.ceil(maxPropostas * 1.1);
    return [0, maxValue];
  };

  const calculateLeftYAxisDomain = () => {
    if (maxConsultas === 0) return [0, 10];

    if (maxPropostas > maxConsultas * 2) {
      const adjustedMax = Math.ceil(maxConsultas * 1.5);
      return [0, adjustedMax];
    }

    return [0, Math.ceil(maxConsultas * 1.1)];
  };

  const transformedData = data.map((item) => ({
    ...item,
    _propostasPagas: item.propostasPagas,
    propostasPagas: item.propostasPagas,
  }));

  return (
    <Box
      borderRadius="xl"
      bg={cardBg}
      p={{ base: 4, md: 6 }}
      boxShadow="md"
      border="1px solid"
      borderColor={borderColor}
      mb={6}
    >
      <Flex justify="space-between" align="center" mb={3} wrap="wrap" gap={2}>
        <Box>
          <Text fontWeight="bold" fontSize="lg">
            {getChartTitle()}
          </Text>
          <Text fontSize="sm" color={useColorModeValue("gray.600", "gray.400")}>
            {data.length} registros no período selecionado
          </Text>
        </Box>

        <HStack
          spacing={0}
          borderRadius="md"
          overflow="hidden"
          border="1px solid"
          borderColor={borderColor}
        ></HStack>
      </Flex>

      <Box
        borderRadius="md"
        overflow="hidden"
        bg={chartBg}
        p={3}
        border="1px solid"
        borderColor={useColorModeValue("gray.100", "gray.700")}
      >
        <ResponsiveContainer width="100%" height={300}>
          <ComposedChart
            data={transformedData}
            margin={{ top: 30, right: 60, left: 30, bottom: 30 }}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
          >
            <CartesianGrid
              strokeDasharray="3 3"
              stroke={gridColor}
              opacity={0.5}
            />
            <XAxis
              dataKey="name"
              tickFormatter={formatXAxis}
              interval={calculateTickInterval()}
              tick={{ fill: axisLabelColor, fontSize: 12 }}
              stroke={axisLabelColor}
              height={50}
              label={{
                value: "Período",
                position: "insideBottom",
                offset: 0,
                fill: axisLabelColor,
                fontSize: 14,
                dy: -10,
              }}
            />
            {/* Left Y-axis for Consultas */}
            <YAxis
              yAxisId="left"
              tick={{ fill: leftAxisColor, fontSize: 12 }}
              stroke={leftAxisColor}
              tickFormatter={formatLeftAxisTick}
              width={60}
              domain={calculateLeftYAxisDomain()}
              label={{
                value: "Consultas",
                angle: -90,
                position: "Left",
                fill: leftAxisColor,
                fontSize: 12,
                fontWeight: "bold",
                dx: -20,
              }}
            />
            {/* Right Y-axis for Propostas Pagas */}
            <YAxis
              yAxisId="right"
              orientation="right"
              tick={{ fill: rightAxisColor, fontSize: 12 }}
              stroke={rightAxisColor}
              tickFormatter={formatRightAxisTick}
              width={60}
              domain={calculateRightYAxisDomain()}
              label={{
                value: "Propostas Pagas",
                angle: 90,
                position: "Right",
                fill: rightAxisColor,
                fontSize: 12,
                fontWeight: "bold",
                dx: 20,
              }}
            />
            <Tooltip
              content={<CustomTooltip />}
              formatter={(value, name) => {
                if (name === "Propostas Pagas") {
                  const originalIndex = transformedData.findIndex(
                    (item) => item.propostasPagas === value
                  );
                  if (originalIndex !== -1) {
                    return transformedData[originalIndex]._propostasPagas;
                  }
                }
                return value;
              }}
            />
            <Legend
              verticalAlign="bottom"
              height={36}
              content={renderLegend}
              wrapperStyle={{
                paddingTop: 15,
                marginBottom: -5,
              }}
            />
            <Bar
              dataKey="consultas"
              fill={barColor}
              name="Consultas"
              radius={[3, 3, 0, 0]}
              animationDuration={800}
              barSize={
                dataGrouping === "daily"
                  ? data.length > 30
                    ? 12
                    : 18
                  : dataGrouping === "weekly"
                    ? data.length > 20
                      ? 18
                      : 24
                    : data.length > 12
                      ? 24
                      : 30
              }
              yAxisId="left"
              legendType="rect"
            />
            <Line
              type="monotone"
              dataKey="propostasPagas"
              stroke={lineColor}
              name="Propostas Pagas"
              strokeWidth={3}
              dot={{ r: 5, strokeWidth: 2, fill: lineColor }}
              activeDot={{ r: 7, strokeWidth: 2 }}
              animationDuration={1200}
              yAxisId="right"
              legendType="line"
            />
            {refAreaLeft && refAreaRight && (
              <ReferenceArea
                x1={refAreaLeft}
                x2={refAreaRight}
                strokeOpacity={0.2}
                fill={brushColor}
              />
            )}
          </ComposedChart>
        </ResponsiveContainer>
      </Box>

      {/* Informações adicionais */}
      {maxPropostas > 0 && maxConsultas > 0 && (
        <Flex mt={3} justify="flex-end" wrap="wrap" gap={4}>
          <Box
            bg={maxPropostasBgColor}
            p={2}
            borderRadius="md"
            borderLeft="4px solid"
            borderColor={lineColor}
          >
            <Text fontSize="sm" fontWeight="medium" color={rightAxisColor}>
              Valor máximo de Propostas Pagas:{" "}
              <strong>{maxPropostas.toLocaleString()}</strong>
            </Text>
          </Box>
          <Box
            bg={maxConsultasBgColor}
            p={2}
            borderRadius="md"
            borderLeft="4px solid"
            borderColor={barColor}
          >
            <Text fontSize="sm" fontWeight="medium" color={leftAxisColor}>
              Valor máximo de Consultas:{" "}
              <strong>{maxConsultas.toLocaleString()}</strong>
            </Text>
          </Box>
        </Flex>
      )}
    </Box>
  );
};

export default CustomChartComponent;
